import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CaptureViewport from '../components/CaptureViewport';
import LiveCameraHelp from '../components/LiveCameraHelp';
import ScannerViewport from '../components/ScannerViewport';
import VerdictOverlay from '../components/VerdictOverlay';
import CountdownOverlay from '../components/CountdownOverlay';
import { useAppState } from '../context/AppContext';
import { useInspectCamera } from '../hooks/useInspectCamera';
import { submitInspection } from '../services/api';
import { isInsecureHttpOrigin, isSafariBrowser } from '../utils/cameraSupport';
import {
  TARGET_DISTANCE_CM,
  DISTANCE_TOLERANCE_CM,
  classifyDistance,
  smoothDistance,
} from '../utils/distance';
import { loadImageFromFile } from '../utils/imageFromFile';
import { measureMarkerDistance } from '../utils/markerDistance';
import { runInspectionOnCanvas } from '../utils/runInspection';
import { evaluateAppearanceRules } from '../utils/evaluateRules';
import { consensusSpots, stabilizeOverlaySpots, trackOverlaySpots } from '../utils/spotStabilize';
import { renderAnnotatedEvidence, canvasToBlob } from '../utils/annotateEvidence';
import './ScanPage.css';

export default function ScanPage() {
  const navigate = useNavigate();
  const { rules, meta } = useAppState();
  const [useCaptureFallback, setUseCaptureFallback] = useState(false);
  const [distanceCm, setDistanceCm] = useState(null);
  const [standMode, setStandMode] = useState(true);
  const [approxDistanceCm, setApproxDistanceCm] = useState(TARGET_DISTANCE_CM);
  const [torchOn, setTorchOn] = useState(false);
  const [markerFound, setMarkerFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [overlaySpots, setOverlaySpots] = useState([]);
  const [verdictFlash, setVerdictFlash] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [error, setError] = useState('');
  const autoTorchTriedRef = useRef(false);

  const fileInputRef = useRef(null);
  const previewRef = useRef('');
  const liveBusyRef = useRef(false);
  const smoothDistRef = useRef(null);
  const overlayHistoryRef = useRef([]);
  const spotTracksRef = useRef([]);

  const targetDistanceCm = rules.targetDistanceCm || TARGET_DISTANCE_CM;
  const toleranceCm = rules.distanceToleranceCm || DISTANCE_TOLERANCE_CM;
  const isCaptureMode = useCaptureFallback;

  const distanceState = useMemo(() => {
    if (isCaptureMode || standMode) {
      return {
        status: 'ready',
        direction: 'ok',
        arrow: '✓',
        label: isCaptureMode
          ? `โหมดถ่ายภาพ · ~${targetDistanceCm} ซม.`
          : `โหมดขาตั้ง · ล็อก ${targetDistanceCm} ซม.`,
        hint: `เป้า ${targetDistanceCm} ± ${toleranceCm} ซม. · สแกนได้`,
        canScan: true,
        deltaCm: 0,
        approx: false,
      };
    }

    // มีการ์ดอ้างอิง → ใช้ระยะวัดจริง
    if (markerFound && distanceCm != null) {
      return {
        ...classifyDistance(distanceCm, targetDistanceCm, toleranceCm, {
          hasMarker: true,
        }),
        approx: false,
      };
    }

    // ไม่มีขาตั้ง + ไม่มีมาร์กเกอร์ → ประมาณระยะมือถือ (สแกนได้)
    return {
      status: 'ready',
      direction: 'ok',
      arrow: '~',
      label: `มือถือ · ประมาณ ${approxDistanceCm} ซม.`,
      hint: 'ขนาดจุดเป็นค่าประมาณ · จัดให้ใกล้ 15 ซม. หรือวางการ์ด 5 ซม. เพื่อล็อกระยะ',
      canScan: true,
      deltaCm: Number((approxDistanceCm - targetDistanceCm).toFixed(1)),
      approx: true,
    };
  }, [
    isCaptureMode,
    standMode,
    distanceCm,
    targetDistanceCm,
    toleranceCm,
    markerFound,
    approxDistanceCm,
  ]);

  function resolvedDistanceCm() {
    if (standMode || isCaptureMode) return targetDistanceCm;
    if (markerFound && distanceCm != null) return distanceCm;
    return approxDistanceCm;
  }

  const camera = useInspectCamera({
    torchEnabled: !isCaptureMode && torchOn,
    enabled: !isCaptureMode,
    autoStart: true,
  });
  const safari = isSafariBrowser();
  const rafRef = useRef(0);
  const showLiveHelp =
    !isCaptureMode &&
    !camera.ready &&
    !camera.needsTap &&
    (Boolean(camera.error) || isInsecureHttpOrigin());

  // พอกล้องพร้อมและรองรับไฟ — เปิดไฟให้อัตโนมัติครั้งแรก (ยังกดปิดได้)
  useEffect(() => {
    if (isCaptureMode || autoTorchTriedRef.current) return;
    if (camera.ready && camera.torchSupported) {
      autoTorchTriedRef.current = true;
      setTorchOn(true);
    }
  }, [isCaptureMode, camera.ready, camera.torchSupported]);

  function setEvidencePreview(url) {
    if (previewRef.current && previewRef.current !== url) {
      URL.revokeObjectURL(previewRef.current);
    }
    previewRef.current = url || '';
    setPreviewUrl(url || '');
  }

  function buildPayload(inspected, mode) {
    return {
      ...inspected,
      rules,
      rollNo: meta.rollNo,
      grade: meta.grade,
      operator: meta.operator,
      capturedAt: new Date().toISOString(),
      device: navigator.userAgent,
      captureMode: mode,
    };
  }

  function showVerdict(payload, evidenceUrl, spotCount) {
    setVerdictFlash({
      verdict: payload.verdict,
      failures: payload.failures || [],
      message: payload.failures?.[0]?.message,
      previewUrl: evidenceUrl || '',
      spotCount: spotCount ?? payload.spotCount ?? payload.spots?.length ?? 0,
    });
  }

  useEffect(() => {
    if (isCaptureMode || !camera.ready || standMode) {
      if (standMode) setMarkerFound(false);
      return undefined;
    }

    smoothDistRef.current = null;
    let last = 0;
    const tick = (ts) => {
      if (ts - last > 250) {
        last = ts;
        const { distanceCm: estimated, marker } = measureMarkerDistance(
          camera.videoRef.current
        );
        setMarkerFound(Boolean(marker));
        if (estimated != null) {
          const smoothed = smoothDistance(smoothDistRef.current, estimated, 0.35);
          smoothDistRef.current = smoothed;
          setDistanceCm(smoothed);
        } else if (!marker) {
          smoothDistRef.current = null;
          setDistanceCm(null);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isCaptureMode, camera.ready, camera.videoRef, standMode]);

  useEffect(() => {
    if (standMode) setDistanceCm(targetDistanceCm);
  }, [standMode, targetDistanceCm]);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  // Live detection — ห้ามอ่านเด็ดขาดถ้ายังระยะไม่พร้อม
  useEffect(() => {
    if (isCaptureMode || !camera.ready || verdictFlash || !distanceState.canScan) {
      if (!distanceState.canScan || isCaptureMode || !camera.ready) {
        setOverlaySpots([]);
        overlayHistoryRef.current = [];
        spotTracksRef.current = [];
      }
      return undefined;
    }

    let cancelled = false;
    const timer = setInterval(async () => {
      if (cancelled || liveBusyRef.current || busy) return;
      if (!distanceState.canScan) {
        setOverlaySpots([]);
        overlayHistoryRef.current = [];
        spotTracksRef.current = [];
        return;
      }
      liveBusyRef.current = true;
      try {
        const frame = await camera.captureFrame();
        if (!frame || cancelled) return;
        const usedDistance = resolvedDistanceCm();
        const inspected = await runInspectionOnCanvas(
          frame.canvas,
          usedDistance,
          rules
        );
        if (cancelled) return;

        const hist = overlayHistoryRef.current.concat([inspected.overlaySpots || []]);
        overlayHistoryRef.current = hist.slice(-5);
        // ยืนยันจากหลายเฟรมก่อน แล้วค่อย track ตามมือสั่น
        const confirmed = stabilizeOverlaySpots(overlayHistoryRef.current, 2, 0.09);
        const tracked = trackOverlaySpots(spotTracksRef.current, confirmed, {
          matchRadius: 0.11,
          confirmHits: 1,
          maxMisses: 7,
          smooth: 0.38,
          dedupeRadius: 0.09,
        });
        setOverlaySpots(tracked);
      } catch (err) {
        // ignore transient CV errors
      } finally {
        liveBusyRef.current = false;
      }
    }, 480);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    isCaptureMode,
    camera.ready,
    camera.captureFrame,
    standMode,
    targetDistanceCm,
    distanceCm,
    rules,
    busy,
    verdictFlash,
    distanceState.canScan,
  ]);

  async function persistAndGo(payload, annotatedBlob, sourceBlob) {
    const saved = await submitInspection({
      result: {
        ...payload,
        grade: meta.grade || '',
        gram: meta.gram || '',
        paperWidth: meta.paperWidth || '',
        operator: meta.operator || meta.inspector || '',
        inspector: meta.inspector || meta.operator || '',
        pdr: meta.pdr || '',
        sizeW: meta.sizeW || '',
        sizeH: meta.sizeH || '',
        size: meta.size || '',
      },
      blob: annotatedBlob,
      sourceBlob,
      fileName: `rollscan-annotated-${Date.now()}.jpg`,
      sourceFileName: `rollscan-source-${Date.now()}.jpg`,
    });
    navigate(`/scan/result/${saved.id}`, { replace: true });
  }

  async function inspectFrame(frame, mode = 'live') {
    const usedDistance = resolvedDistanceCm();

    const inspected = await runInspectionOnCanvas(frame.canvas, usedDistance, rules);
    const payload = buildPayload(inspected, mode);
    const annotated = await renderAnnotatedEvidence(frame.canvas, {
      overlaySpots: inspected.overlaySpots || [],
      verdict: payload.verdict,
      spotCount: payload.spotCount ?? inspected.spots?.length ?? 0,
      maxSpotDiameterMm: rules.maxBlackSpotDiameterMm,
    });

    setOverlaySpots(inspected.overlaySpots || []);
    setEvidencePreview(annotated.url);
    showVerdict(
      payload,
      annotated.url,
      payload.spotCount ?? inspected.spots?.length ?? 0
    );

    await persistAndGo(
      {
        ...payload,
        distanceCm: usedDistance,
      },
      annotated.blob,
      frame.blob || (await canvasToBlob(frame.canvas))
    );
  }

  async function handleScan() {
    // ห้ามถ่าย/อ่าน ถ้ายังระยะไม่พร้อม
    if (!camera.ready || busy || countdown != null) return;
    if (!distanceState.canScan) {
      setError('ระยะยังไม่พร้อม — ห้ามอ่านจุด จนกว่าจะขึ้น READY / ระยะ OK');
      setOverlaySpots([]);
      return;
    }
    setBusy(true);
    setError('');
    setVerdictFlash(null);

    // ล็อกจุดที่เห็นบนจอตอนกดสแกน — ผลสุดท้ายต้องเท่านี้
    const liveConfirmed = (overlaySpots || []).map((s) => ({ ...s }));

    const prevTorch = torchOn;
    try {
      // Force torch ON for capture
      if (camera.torchSupported) {
        setTorchOn(true);
        await new Promise((r) => setTimeout(r, 250));
      }

      for (let n = 2; n >= 1; n -= 1) {
        setCountdown(n);
        await new Promise((r) => setTimeout(r, 1000));
      }
      setCountdown(null);

      const usedDistance = resolvedDistanceCm();

      const runs = [];
      // ถ่ายหลายเฟรมสำหรับรูปหลักฐาน + เสริมขนาดจุด
      for (let i = 0; i < 4; i += 1) {
        const frame = await camera.captureFrame();
        if (!frame) throw new Error('จับภาพไม่สำเร็จ');
        const inspected = await runInspectionOnCanvas(
          frame.canvas,
          usedDistance,
          rules
        );
        runs.push({ frame, inspected });
        if (i < 3) await new Promise((r) => setTimeout(r, 100));
      }

      // เห็นบนจอกี่จุด → ผลต้องกี่จุด (ไม่ตัดทิ้งจาก consensus)
      const fromCapture = consensusSpots(
        runs.map((r) => r.inspected.overlaySpots || []),
        0.1
      );
      const stableOverlays =
        liveConfirmed.length > 0 ? liveConfirmed : fromCapture;

      const allSpots = runs.flatMap((r) => r.inspected.spots || []);
      const stableSpots = stableOverlays.map((ov, idx) => {
        const cx = ov.nx + ov.nw / 2;
        const cy = ov.ny + ov.nh / 2;

        const candidates = runs
          .flatMap((r) =>
            (r.inspected.overlaySpots || []).map((o) => ({
              o,
              spots: r.inspected.spots,
            }))
          )
          .filter(
            ({ o }) =>
              Math.hypot(o.nx + o.nw / 2 - cx, o.ny + o.nh / 2 - cy) < 0.12
          );

        const source = candidates[0];
        const matchedSpot =
          source?.spots?.find((s) => s.id === source.o.id) ||
          allSpots.find((s) => Math.abs(s.diameterMm - ov.diameterMm) < 0.35) ||
          null;

        return (
          matchedSpot || {
            id: ov.id || `stable-${idx + 1}`,
            type: ov.type || (ov.isKnot ? 'wood_knot' : 'black_spot'),
            diameterMm: ov.diameterMm,
            xMm: null,
            yMm: null,
          }
        );
      });

      const defects = stableSpots
        .filter((s) => s.type === 'wood_knot')
        .map((s) => ({
          type: 'wood_knot',
          relatedSpotId: s.id,
          diameterMm: s.diameterMm,
        }));

      const sampleAreaSqFt =
        runs[0].inspected.sampleAreaSqFt ||
        runs[0].inspected.metrics?.sampleAreaSqFt ||
        1;

      const judged = evaluateAppearanceRules(stableSpots, defects, {
        maxSpotDiameterMm: rules.maxBlackSpotDiameterMm,
        largeSpotThresholdMm: rules.largeSpotThresholdMm,
        minDistanceBetweenLargeSpotsCm: rules.minDistanceBetweenLargeSpotsCm,
        maxBlackSpotsPerSqFt: rules.maxBlackSpotsPerSqFt,
        sampleAreaSqFt,
      });

      const evidenceFrame = runs[1]?.frame || runs[0].frame;

      const payload = buildPayload(
        {
          ...judged,
          overlaySpots: stableOverlays,
          metrics: {
            ...(runs[1]?.inspected.metrics || runs[0].inspected.metrics || {}),
            spotCount: judged.spotCount,
            spotsPerSqFt: judged.spotsPerSqFt,
            framesUsed: 4,
            stableCount: stableSpots.length,
            torchOnCapture: Boolean(camera.torchSupported),
          },
          sampleAreaSqFt: judged.sampleAreaSqFt,
          distanceCm: usedDistance,
        },
        'live-stable'
      );

      // วาดกรอบจุดลงบนรูปก่อนบันทึกเข้า storage
      const annotated = await renderAnnotatedEvidence(evidenceFrame.canvas, {
        overlaySpots: stableOverlays,
        verdict: payload.verdict,
        spotCount: judged.spotCount,
        maxSpotDiameterMm: rules.maxBlackSpotDiameterMm,
      });

      setOverlaySpots(stableOverlays);
      setEvidencePreview(annotated.url);
      showVerdict(payload, annotated.url, judged.spotCount);

      const sourceBlob = await canvasToBlob(evidenceFrame.canvas);

      await persistAndGo(payload, annotated.blob, sourceBlob);
    } catch (err) {
      setError(err.message || 'ถ่ายไม่สำเร็จ');
    } finally {
      setCountdown(null);
      // keep torch on after capture if user had it on; otherwise leave on (useful for QC)
      if (!prevTorch && camera.torchSupported) {
        // leave torch on after shoot — user can turn off
        setTorchOn(true);
      }
      setBusy(false);
    }
  }

  function openNativeCamera() {
    fileInputRef.current?.click();
  }

  async function handleFileCapture(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    setError('');
    try {
      const frame = await loadImageFromFile(file);
      await inspectFrame(frame, 'capture');
    } catch (err) {
      setError(err.message || 'ถ่ายภาพไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  function dismissVerdict() {
    setVerdictFlash(null);
  }

  return (
    <div className="scan-page">
      <div className="scan-body">
      <section className="scan-workspace">
        <div className="scan-camera-wrap">
          {isCaptureMode ? (
            <div style={{ position: 'relative' }}>
              <CaptureViewport
                previewUrl={previewUrl}
                targetDistanceCm={targetDistanceCm}
                onCaptureClick={openNativeCamera}
                inputRef={fileInputRef}
                onFileChange={handleFileCapture}
              />
              {verdictFlash && (
                <VerdictOverlay
                  verdict={verdictFlash.verdict}
                  message={verdictFlash.message}
                  failures={verdictFlash.failures}
                  previewUrl={verdictFlash.previewUrl}
                  spotCount={verdictFlash.spotCount}
                  onDismiss={dismissVerdict}
                />
              )}
            </div>
          ) : (
            <>
              <div style={{ position: 'relative' }}>
                <ScannerViewport
                  videoRef={camera.videoRef}
                  distanceState={distanceState}
                  targetDistanceCm={targetDistanceCm}
                  toleranceCm={toleranceCm}
                  starting={camera.starting}
                  cameraReady={camera.ready}
                  cameraError={camera.error}
                  needsTap={camera.needsTap}
                  overlaySpots={overlaySpots}
                  torchOn={torchOn}
                  torchSupported={camera.torchSupported}
                  onToggleTorch={() => setTorchOn((v) => !v)}
                  onStartCamera={camera.start}
                />
                <CountdownOverlay value={countdown} />
                {verdictFlash && (
                  <VerdictOverlay
                    verdict={verdictFlash.verdict}
                    message={verdictFlash.message}
                    failures={verdictFlash.failures}
                    previewUrl={verdictFlash.previewUrl}
                    spotCount={verdictFlash.spotCount}
                    onDismiss={dismissVerdict}
                  />
                )}
              </div>
              {showLiveHelp && (
                <LiveCameraHelp onUseCaptureFallback={() => setUseCaptureFallback(true)} />
              )}
              {safari && camera.ready && !camera.torchSupported && (
                <div className="badge warn scan-inline-msg" style={{ alignSelf: 'flex-start' }}>
                  Safari/iPhone ไม่รองรับเปิดไฟจากเว็บ — ใช้แฟลชจากกล้องเครื่องแทน
                </div>
              )}
            </>
          )}
        </div>

        {camera.error && !isCaptureMode && (
          <div className="card scan-inline-msg" style={{ color: 'var(--ng)' }}>{camera.error}</div>
        )}
        {error && <div className="card scan-inline-msg" style={{ color: 'var(--ng)' }}>{error}</div>}

        {!isCaptureMode && (
          <div className="scan-actions">
            <button
              className="btn btn-primary btn-scan-main"
              disabled={
                !camera.ready ||
                !distanceState.canScan ||
                busy ||
                countdown != null
              }
              onClick={handleScan}
            >
              {countdown != null
                ? `ถ่ายใน ${countdown}...`
                : busy
                  ? 'กำลังบันทึก...'
                  : !distanceState.canScan
                    ? 'รอระยะให้ OK'
                    : 'สแกน'}
            </button>
          </div>
        )}

        {isCaptureMode && busy && (
          <div className="card scan-inline-msg muted">กำลังตรวจและบันทึกรูป...</div>
        )}
      </section>
      </div>
    </div>
  );
}
